/**
 * English display labels for every feature code that appeared in the legacy
 * KundenBillingFrame.java Art dropdown (KundenBillingFrame.java:157-245).
 *
 * Since EB-3887 the primary source of the English text is the Localization API
 * `featureShowname/defTarif` i18n bag (English id 2). This map is the documented
 * English FALLBACK used when the English bag returns nothing for a code (older
 * codes, untranslated brands) — resolved in `feature-texts.util.ts` /
 * `resolveFeatureTexts` (EB-4502; group labels still use `buildBilingualLabel`).
 *
 * English text priority in the Add Billing dialog and Billing grid:
 *   0. ENGLISH_OVERRIDES[code]               (feature-texts.util.ts — wrong bag values, e.g. gut_credit)
 *   1. featureShowname/defTarif i18n bag     (brand + language specific, primary)
 *   2. FEATURE_LABELS_EN[code]               (this map — deterministic fallback)
 *   3. BillingGroupFeature.showName          (backend-provided label)
 *   4. raw feature code                      (last resort)
 *
 * Duplicate codes across legacy groups (`gut_gut`, `brief_scan`, `catering`):
 * first-occurrence label wins.
 */
export const FEATURE_LABELS_EN: Record<string, string> = {
  // 1. Credits
  gut_gut: 'Credit, general',
  gut_grund: 'Credit, base fee',

  // 3. Secretarial Services
  callsekret: 'Secretarial Service calls',
  callsekret_1min: 'Secretarial Service calls (per minute)',
  /**
   * `_NE` = "Nebenzeit": the off-hours twin of a feature (billed outside the tariff's
   * business-hours band), NOT a secondary account. The KV has no label for them; texts are
   * copied from localization featureShowname/defTarif, language 2 (EB-3888 snapshot
   * data/localization-en-lang2.json).
   *
   * @ticket EB-4384
   */
  callsekret_NE: 'Secretarial services call acceptance',
  callsekret_1min_NE: 'Secretarial services call acceptance',
  cc_sekret: 'CC Secretarial Service calls',
  cc_sekret_1min: 'CC Secretarial Service calls (per minute)',
  cc_sekret_NE: 'Secretarial service (5 min.)',
  cc_sekret_1min_NE: 'Secretarial service (1 min.)',
  ab_einrichtung: 'Recording of an answering message',

  // 5. Post Service
  brief_empf: 'Letter acceptance / forwarding per letter',
  paket_empf: 'Parcel acceptance / forwarding (<5kg) per parcel',
  paket_empf_zuschlag: 'Parcel acceptance / forwarding surcharge (>5kg) per kg',
  porto: 'Postage (letter/parcel)',
  paket_lagerung: 'Parcel storage per parcel and day (<5kg)',
  paket_lagerung_zuschlag: 'Parcel storage surcharge (>5kg) per kg and day',
  brief_vers: 'Letter dispatch via web interface',
  brief_scan: 'Scan service',
  brief_query: 'Phone inquiry for incoming mail',

  // 6. Room Service
  room_DUS: 'Room booking with Domicile (Dussmann)',
  room_light_DUS: 'Room booking without Domicile (Dussmann)',
  storno: 'Cancellation fee for room booking',
  storno_day_before_DUS: 'Cancellation fee for room booking (Dussmann, day before)',
  storno_same_day_DUS: 'Cancellation fee for room booking (Dussmann, same day)',
  room: 'Room booking with Domicile',
  room_light: 'Room booking without Domicile',
  office_service: 'General office tasks',
  print_color: 'Color print A4',
  beamer: 'Projector',
  beamer_1day: 'Projector — full day',
  beamer_4h: 'Projector — half day',
  beamer_1h: 'Projector — per hour',
  laptop_1day: 'Laptop — full day',
  laptop_4h: 'Laptop — half day',
  laptop_1h: 'Laptop — per hour',
  monitor_full_hd: 'Monitor Full-HD',
  monitor_full_hd_1day: 'Monitor Full-HD — full day',
  monitor_full_hd_4h: 'Monitor Full-HD — half day',
  monitor_full_hd_1h: 'Monitor Full-HD — per hour',
  konferenztelefon_1day: 'Conference phone — full day',
  konferenztelefon_4h: 'Conference phone — half day',
  konferenztelefon_1h: 'Conference phone — per hour',
  moderationskoffer: 'Moderation kit',
  botengang: 'External courier run',
  office_material: 'Office supplies',
  fax_office: 'Fax in/out on-site',
  drinks_juice: 'Drinks — juices',
  coffee: 'Drinks — tea/coffee',
  coffee_free: 'Drinks — tea/coffee (free)',
  drinks_water: 'Drinks — water',
  drinks: 'Drinks',
  snacks: 'Snacks',
  'copy_b/w': 'Copy b/w A4',
  copy_service: 'Copy service operation',
  'print_b/w': 'Laser print b/w A4',
  flex_office_1day: 'Flex office rent — full day',
  flex_office_4h: 'Flex office rent — half day',
  flex_office_1h: 'Flex office rent — per hour',
  catering_service: 'Catering service',
  other_service: 'Other services',
  catering: 'Meals / catering',
  storno_day_before: 'Cancellation fee for room booking (day before)',
  storno_same_day: 'Cancellation fee for room booking (same day)',
  phone_germany: 'On-site phone charges (Germany)',
  text_german: 'Word processing (German)',
  text_foreign: 'Word processing (foreign language)',
  wlan: 'WiFi usage (per day)',
  additionalChargeOvertime: 'Surcharge for after-hours rental',

  // 7. NKohl
  nkohl_gk_satz1: 'Court costs — VAT rate 1 (16%)',
  nkohl_gk_satz0: 'Court costs — VAT rate 0 (0%)',

  // 8. Dictation Service
  diktat_overnight: 'Rate Overnight (per minute)',
  diktat_3days: 'Rate 3 business days (per minute)',
  diktat_5days: 'Rate 5 business days (per minute)',

  // 9. Hold Music Production
  mohCreateStandard: 'Hold music — Standard',
  mohCreatePremium: 'Hold music — Premium',

  // 10. Logo Production
  logo_1proposal: 'Logo — 1 proposal',
  logo_3proposal: 'Logo — 3 proposals',
  logo_5proposal: 'Logo — 5 proposals',
  logo_claim: 'Logo extension: claim',
  logo_extra: 'Extra logo',

  // 11. Special Services
  evn: 'Itemised billing (EVN) creation',
  form_erst: 'Form creation',

  // 12. Special Promotions
  aktion_tel: 'Special promotion — Phone secretariat',
  aktion_ga: 'Special promotion — Business address',

  // 13. Setup Fees
  setup_number: 'Setup — local number',
  setup_firmenschild: 'Setup — company sign',

  // 14. Returned Mail
  adress_erm5: 'Address research — €5',
  adress_erm10: 'Address research — €10',
  adress_erm22: 'Address research — €22',

  // 15. Additional Fees
  fees_subsequent: 'Subsequent invoicing fees',
  fee_CompanyRegistry: 'Commercial register extract fee',
  fees_additional: 'Additional fees',
  packageUpgrade: 'Package upgrade',

  // 16. Commission
  provisionKvm: 'Customer referral commission',

  // 17. Multiphoneservice
  training_fee: 'Training flat rate (1 hour)',
  object_mapping: 'Object mapping',
  backoffice: 'Backoffice project (1 hour)',

  // 18. Office Club Products
  sekret: 'Secretarial service (5 min.)',
  tea: 'Tea',
  bitebox: 'BiteBox snack',
  charitea: 'ChariTea',
  annahme_von_einschreiben: 'Acceptance of registered mail',
  locker: 'Locker',
  telefon: 'Phone (device)',
  firmenschild: 'Company sign',
  paket_handling: 'Letter dispatch handling',
  kurier: 'Courier service (€)',
  post_vers: 'Franking / dispatch (pcs)',
  room_1h: 'Conference room use (1h)',
  scan: 'Scanning documents (pcs)',
  fritz_kola: 'Fritz-Kola (various)',
  club_mate: 'Club Mate',
  saft_bio: 'Organic juice',
  bier_div: 'Beer',
  sekt: 'Sparkling wine',
  illy_kaffee: 'Coffee / Americano',
  illy_michkaffee: 'Latte',
  illy_cappuccino: 'Cappuccino',
  illy_latte_macchiato: 'Latte Macchiato',
  illy_espresso_doppio: 'Espresso doppio',
  illy_espresso: 'Espresso',
  trinkschokolade_div: 'Hot chocolate',
  extra_day: 'Extra day',
  'copy_A3_b/w': 'Copy b/w A3',
  green_card: 'Extension / reissue Club Card',
  postw_taglich: 'Mail forwarding',
  print_color_A3: 'Color print A3',
  duplo: 'Duplo',
  kinderriegel: 'Kinder Riegel',
  balisto: 'Balisto',
  kuchen_div: 'Cake (various)',
  getranke: 'Hot drinks flat rate',
  whiteboard: 'Whiteboard / flipchart',
  funkmikrofone: 'Wireless microphone',
  rednerpult: 'Lectern',
  gebackteller: 'Pastry plate',
  coffee_1l: 'Coffee — per pot (1L)',
  tea_1l: 'Tea — per pot (1L)',
  lemonaid: 'Lemonaid (various)',
  twixbistro: 'Twix',
  trial_day: 'Day pass',
  monitor_hosting: 'Monitor hosting',
  cowo_club_247_access: 'Office Club 24/7 access',
  internet_lan: 'Internet via LAN',
  briefumschlag: 'Envelope',
  einrichtungsgebuehr: 'Setup fee',
  wasa_einzeln: 'Wasa (individual)',
  sms_versand: 'SMS dispatch',
  letter_black_white_page: 'Letter page (b/w)',
  letter_colored_page: 'Letter page (color)',
  letter_envelope_c5: 'Envelope (DIN Lang)',
  letter_envelope_c4: 'Envelope (C4)',
  letter_registered_insertion: 'Registered mail (insertion)',
  local_phonenumber: 'Local phone number',
  '0800_phonenumber': '0800 phone number',
  '0180-5_phonenumber': '0180-5 phone number',
  forwarding_fixed_line: 'Forwarding — German landline / min',
  forwarding_europe_and_usa: 'Forwarding — Europe & USA / min',
  forwarding_mobile: 'Forwarding — mobile / min',
  forwarding_world: 'Forwarding — rest of world / min',
  accepted_call_pickup: 'Call pickup — premium number (landline)',
  accepted_call_min: 'Call minute — premium number (landline)',
  postal_invoice: 'Invoice by mail',
  event: 'Event',
  knoppers: 'Knoppers',
  kitkat: 'KitKat',
  ausgleichfehlbestand: 'Cash deficit balance',
  getraenke_basic: 'Drinks flat rate per person',
  meeting_manuell: 'Meeting',
  personal: 'Staff',
  barzahlung_rng: 'Cash payment invoice',
  parkplatz: 'Parking space',
  handling_brief: 'Letter handling',
  flex_member: 'Flex membership',
  freedrink_tariff_membership: 'Free drink — membership rate',
  freemonth_user_vacation: 'Free month — vacation mode (membership rate)',
  desk: 'Desk',
  infomail_absender: 'Infomail service',
  flat_white: 'Flat White',
  fruits: 'Fruit basket',
  mmsklein: 'M&Ms',
  maske: 'Face mask',
  zusatz_empfaenger: 'Additional stamp / recipient',
  aufwand: 'Expense allowance',
  nespresso: 'Nespresso',
  huoberbrezeln: 'Huober Minis — pretzels',
  specialdrink: 'Seasonal special drink',
  pausensnack: 'Break snack per person',
  fruehstueckspauschale: 'Breakfast flat rate per person',
  verfplegungspauschale: 'Catering flat rate per person',
  cremant: 'Crémant',
  moebel: 'Rental furniture',
  telephone_forwarding: 'Phone forwarding / pickup',
  zusaetzlicher_arbeitsplatz: 'Additional workstation',
  postal_handling: 'Weekly mail batch delivery',
  postal_collection: 'Mail self-pickup (storage fee)',
  video_konferenz: 'Video conference system',
  moebel_verkauf: 'Furniture / inventory sale',
  telefonbuch_eintrag: 'Phone book entry',
  cleaning: 'Final cleaning',
};
